import { useDebounce } from '@/hooks/debounce';
import { usePeopleLookup } from '@/store/people/hooks';
import type { Person } from '@/store/people/types';
import {
  Button,
  Popover,
  PopoverTrigger,
  PopoverContent,
  CircularProgress,
  Input,
  Modal,
  ModalContent,
} from '@/components/ui';
import { useState, useRef, useEffect } from 'react';
import { useBreakpoint } from '@/hooks/screen';
import { Empty } from '../empty';
import { StandardIcon } from '../icon';
import { PersonItem } from '../output/person';
import { useDrawerActions } from '@/store/drawer';
import { MemberStatusChip } from '../output/memberstatus';
import { cn } from '@/lib/cn';

export interface PeopleQuickSearchProps {
  className?: string;
  inputClassName?: string;
  compactButtonClassName?: string;
}

export function PeopleQuickSearch({
  className,
  inputClassName,
  compactButtonClassName,
}: PeopleQuickSearchProps) {
  const [open, setIsOpen] = useState(false);
  const closing = useRef(false);
  const breakpoint = useBreakpoint();
  const isSmallScreen = breakpoint === 'xs' || breakpoint === 'sm';
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 400);
  const { people, loading, empty } = usePeopleLookup({ search });
  const drawerActions = useDrawerActions();

  const setOpen = (value: boolean) => {
    setIsOpen(value);
    if (!value) {
      closing.current = true;
      setTimeout(() => (closing.current = false), 200);
      setSearchInput('');
    } else {
      closing.current = false;
    }
  };

  useEffect(() => {
    if (search) {
      setOpen(true);
    }
  }, [search]);

  const onButtonPress = () => {
    if (!closing.current) {
      setOpen(true);
    }
  };

  const onClosePress = () => {
    setOpen(false);
    closing.current = false;
  };

  const onPersonClick = (item: Person) => {
    console.log('person click %s', item.id);
    drawerActions.setCurrentPerson(item);
    drawerActions.setCurrentComment(null);
    drawerActions.setCurrentMention(null);
    drawerActions.setCurrentTab('profile');
    drawerActions.setOpen(true);
    setOpen(false);
  };

  const extraClassNames = open
    ? //TODO: ? 'fixed top-0 left-0 right-0 h-[100px] px-2 py-1 z-20 bg-white'
      ''
    : '';

  const endContent = loading ? (
    <CircularProgress aria-label='Loading...' size='sm' />
  ) : undefined;

  return (
    <>
      {open && isSmallScreen && (
        <Modal isOpen={open} onOpenChange={setOpen}>
          <ModalContent className='flex h-[min(50vh,600px)] max-h-[min(50vh,600px)] min-w-[360px] flex-col overflow-hidden rounded-xl border border-slate-200 bg-slate-50 p-0 shadow-lg'>
            <div className='flex min-h-0 w-full flex-1 flex-col'>
              <div className='w-full shrink-0 px-4 pt-4'>
                <Input
                  value={searchInput}
                  onValueChange={setSearchInput}
                  className={inputClassName}
                  wrapperClassName='bg-white rounded-lg'
                  placeholder='Search'
                  endContent={endContent}
                  isClearable
                  autoFocus
                  autoComplete='off'
                  autoCorrect='off'
                  startContent={
                    <StandardIcon id='mdi:search' className='text-slate-400' />
                  }
                />
              </div>
              <div className='min-h-0 flex-1 overflow-y-auto px-4'>
                <Content
                  loading={loading}
                  empty={empty}
                  searchInput={searchInput}
                  people={people}
                  onPersonClick={onPersonClick}
                />
              </div>
              <div className='flex w-full shrink-0 justify-center border-t border-slate-200 pb-4 pt-3'>
                <Button
                  variant='light'
                  onPress={onClosePress}
                  className='gap-2 text-xs font-semibold uppercase tracking-wide text-slate-700'
                  startContent={<StandardIcon id='mdi:close' size={18} />}
                >
                  Close search
                </Button>
              </div>
            </div>
          </ModalContent>
        </Modal>
      )}
      <div
        className={`flex flex-col items-end md:min-w-[280px] md:items-center ${className} ${extraClassNames}`}
      >
        <div className='flex w-full flex-row items-center'>
          <Popover
            isOpen={open}
            onOpenChange={(value) => setOpen(value)}
            shouldCloseOnScroll={false}
            wrapperClassName='hidden w-full min-w-0 md:block md:w-auto md:min-w-[280px]'
          >
            <PopoverTrigger>
              <Input
                className={`${inputClassName} hidden w-full md:block`}
                wrapperClassName='rounded-xl border-0 bg-slate-100 min-h-9'
                placeholder='Search'
                isClearable
                isReadOnly
                autoComplete='off'
                autoCorrect='off'
                startContent={
                  <StandardIcon id='mdi:search' className='text-slate-400' />
                }
              />
            </PopoverTrigger>
            <PopoverContent className='left-0 right-0 top-0 mt-0 w-full min-w-[360px] rounded-xl border border-slate-200 bg-slate-50 p-0 shadow-lg'>
              <div className='flex w-full flex-col items-center'>
                <div className='w-full px-4 pt-4'>
                  <Input
                    value={searchInput}
                    onValueChange={setSearchInput}
                    className={inputClassName}
                    wrapperClassName='bg-white rounded-lg'
                    placeholder='Search'
                    endContent={endContent}
                    isClearable
                    autoFocus
                    autoComplete='off'
                    autoCorrect='off'
                    startContent={
                      <StandardIcon
                        id='mdi:search'
                        className='text-slate-400'
                      />
                    }
                  />
                </div>
                <div className='mb-4 mt-4 flex min-h-40 w-full min-w-72 flex-col items-center px-4'>
                  <Content
                    {...{
                      loading,
                      empty,
                      searchInput,
                      people,
                      onPersonClick,
                    }}
                  />
                </div>
                <div className='flex w-full justify-center border-t border-slate-200 pb-4 pt-3'>
                  <Button
                    variant='light'
                    onPress={onClosePress}
                    className='gap-2 text-xs font-semibold uppercase tracking-wide text-slate-700'
                    startContent={<StandardIcon id='mdi:close' size={18} />}
                  >
                    Close search
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
          <div className='flex-grow md:hidden' />
          <Button
            className={cn('md:hidden', compactButtonClassName)}
            variant='light'
            isIconOnly
            onPress={onButtonPress}
            aria-label='Search'
          >
            <StandardIcon id='mdi:search' size={24} />
          </Button>
        </div>
      </div>
    </>
  );
}

function Content({
  loading,
  empty,
  searchInput,
  people,
  onPersonClick,
}: {
  loading: boolean;
  empty: boolean;
  searchInput: string;
  people: Person[];
  onPersonClick: (item: Person) => void;
}) {
  if (loading) {
    return <CircularProgress className='mt-4 self-center' />;
  }

  if (!searchInput) {
    return (
      <div className='flex flex-col items-center gap-6 px-4 py-8'>
        <div className='relative flex h-32 w-32 items-center justify-center'>
          <div className='flex h-24 w-24 items-center justify-center rounded-full border-2 border-slate-200 bg-white'>
            <StandardIcon
              id='mdi:magnify'
              className='text-slate-400'
              size={48}
            />
          </div>
          <div className='absolute bottom-4 right-4 flex h-8 w-8 items-center justify-center rounded-full bg-blue-100'>
            <StandardIcon
              id='mdi:information-outline'
              className='text-primary'
              size={16}
            />
          </div>
        </div>
        <div className='flex max-w-xs flex-col items-center gap-2 text-center'>
          <h3 className='text-base font-bold text-slate-800'>
            Search results will be shown here
          </h3>
          <p className='text-sm leading-relaxed text-slate-500'>
            Start typing to find people, groups, or events in MinistryHub. Your
            results will update in real-time.
          </p>
        </div>
      </div>
    );
  }

  if (empty) {
    return (
      <div className='w-full'>
        <Empty description='No results' icon='mdi:search' />
      </div>
    );
  }

  return (
    <div className='max-h-auto flex w-full flex-col overflow-y-auto sm:max-h-[30vh] md:mt-0'>
      {people.map((item) => (
        <div
          className='mt-1 flex flex-row items-center gap-3 rounded-lg bg-default-100 p-2'
          key={item.id}
          role='button'
          tabIndex={0}
          onClick={() => onPersonClick(item)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onPersonClick(item);
            }
          }}
        >
          <PersonItem
            item={item}
            email={true}
            phone={false}
            className='min-w-0 flex-1 cursor-pointer'
            nameClassName='font-semibold'
            onPress={() => onPersonClick(item)}
          />
          {item.data.member_status ? (
            <MemberStatusChip
              id={item.data.member_status}
              className='shrink-0'
            />
          ) : null}
        </div>
      ))}
    </div>
  );
}
